'use client'

import type { ExperimentClient } from '@amplitude/experiment-js-client'
import { BuilderBlocks } from '@builder.io/react'
import { uniqueId } from 'lodash'
import { useSearchParams } from 'next/navigation'
import { type ReactNode } from 'react'
import React from 'react'

import { BODiAccessConditionalContent } from '@src/components/ConditionalBox/BODiAccessConditionalContent'
import { LoggedInConditionalContent } from '@src/components/ConditionalBox/LoggedInConditionalContent'
import { SKUinCartConditionalContent } from '@src/components/ConditionalBox/SKUinCartConditionalContent'
import { ShopifyEnabledConditionalContent } from '@src/components/ConditionalBox/ShopifyEnabledConditionalContent'
import { useLocale } from '@src/i18n/LocaleProvider'
import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { useExperimentContext } from '@src/utils/experimentProvider'
import { cn } from '@src/utils/tailwind'

export interface SkuFilter {
  skus: string[]
  andOr: 'Any' | 'All'
}

type ContainerProps = React.HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode[] | ReactNode
  includedChildren?: any[]
  visibilityBasedOn?:
    | 'locale'
    | 'experiment'
    | 'queryParams'
    | 'loggedIn'
    | 'hasBodiAccess'
    | 'sku'
    | 'shopifyEnabled'
  attributes?: any
  experimentFlag: string
  experimentVariant: string
  showAsOff: boolean
  showIfLoggedIn: boolean
  showIfHasBodiAccess: boolean
  showIfShopifyEnabled: boolean
  queryParams: string
  matchType: string
  skusInOut: boolean
  anyOrAll: 'Any' | 'All'
  skus: string[]
}
// Checks if user is in experiment and variant, and decides whether to render content in box.
const isUserInExperiment = (
  experimentList: any, // list of all variants/initial variants from amplitude
  flag: string, // builder value
  variant: string, // builder value
  showAsOff: boolean = false, // builder value
  experimentClient: ExperimentClient | null = null // useExperimentContext.experimentClient
) => {
  let showExperiment = false
  // Compare conditional box's flag to list of flags from amplitude
  const flags = Object.keys(experimentList)

  const userInExperiment = flags.includes(flag)
  // Check if variant matches user's assigned variant.

  const userInVariant = Object.prototype.hasOwnProperty.call(experimentList, flag)
    ? experimentList[flag].key === variant
    : false

  /*  If user is in experiment and variant, show the conditional box and fire exposure event.
  Else, if Show As Off is true and user is not in experiment(it was not rolled out), show the box.
  */

  if (userInExperiment && userInVariant) {
    showExperiment = true
    if (experimentClient) {
      exposeExperiment(experimentClient, flag)
    }
  } else if (!userInExperiment && showAsOff) {
    showExperiment = true
  }
  return showExperiment
}
const exposeExperiment = (experimentClient: ExperimentClient, flag: string) => {
  experimentClient.variant(flag)
  experimentClient.exposure(flag)
}

const experimentLabel = (flag: string, variant: string) => {
  return (
    <div
      className="pointer-events-none absolute top-0 right-9 z-50 bg-yellow-600 px-2 py-4 text-body-base text-black opacity-70"
      key={flag + '.' + variant}
    >
      Experiment:{flag}:{variant}
    </div>
  )
}
const queryLabel = (queryParam: string) => {
  return (
    <div
      className="pointer-events-none absolute top-0 right-9 z-50 bg-green-600 px-2 py-4 text-body-base text-black opacity-70"
      key={queryParam}
    >
      Query Param:{queryParam}
    </div>
  )
}

export const ConditionalBox = ({
  className,
  children,
  visibilityBasedOn,
  includedChildren,
  experimentFlag,
  experimentVariant,
  showAsOff,
  showIfLoggedIn,
  showIfHasBodiAccess,
  showIfShopifyEnabled,
  queryParams,
  matchType,
  skusInOut,
  anyOrAll,
  skus,
  ...props
}: ContainerProps) => {
  const { builderBlock, attributes } = props as any

  const searchParams = useSearchParams()

  const shouldRenderBuilderBlocks = () => {
    return React.Children.count(children) <= 0
  }
  const experimentContext = useExperimentContext()
  const userExperiments = experimentContext?.allVariants
  const { locale } = useLocale()

  const isLocationHidden = (props as any)?.[`locale_${locale.path}`]
  const { edit } = getBuilderPreviewMode()

  //Search for query params in URL and return true if the query param exists.
  const hasQueryParam = (matchType: string, queryParam: string) => {
    const search = searchParams.toString()
    switch (matchType) {
      case 'contains':
        return search.search(queryParam) !== -1

      case 'does not contain':
        return search.search(queryParam) === -1

      case 'regEx':
        const regEx = new RegExp(queryParam, 'gi')
        return regEx.test(search)
      default:
        break
    }
  }

  const localeConditionalContent = (
    <div
      {...attributes}
      key={attributes?.key}
      className={cn('size-auto', className, attributes?.className, {
        'grayscale opacity-35': isLocationHidden && edit,
        hidden: isLocationHidden && !edit,
      })}
      style={{ ...attributes?.style }}
      suppressHydrationWarning
    >
      {shouldRenderBuilderBlocks()
        ? includedChildren?.map((child, index) => (
            <BuilderBlocks
              key={uniqueId()}
              parentElementId={builderBlock?.id}
              dataPath={`component.options.includedChildren.${index}.item`}
              blocks={child.item}
              style={{ minHeight: 'auto !important' }}
            />
          ))
        : children}
    </div>
  )
  const queryParamsConditionalContent = (
    <div
      {...attributes}
      key={attributes?.key}
      className={cn('size-auto', className, attributes?.className, {
        'border-4 border-green-600 relative': queryParams && edit,
      })}
      style={{ ...attributes?.style }}
      suppressHydrationWarning
    >
      {/* Adds experiment label in edit mode */}
      {edit && queryLabel(queryParams)}
      {shouldRenderBuilderBlocks()
        ? includedChildren?.map((child, index) => (
            <BuilderBlocks
              key={uniqueId()}
              parentElementId={builderBlock?.id}
              dataPath={`component.options.includedChildren.${index}.item`}
              blocks={child.item}
              style={{ minHeight: 'auto !important' }}
            />
          ))
        : children}
    </div>
  )
  const experimentConditionalContent = (
    <div
      {...attributes}
      key={attributes?.key}
      className={cn('size-auto', className, attributes?.className, {
        'border-4 border-yellow-600 relative': experimentFlag && edit,
      })}
      style={{ ...attributes?.style }}
      suppressHydrationWarning
    >
      {/* Adds experiment label in edit mode */}
      {edit && experimentLabel(experimentFlag, experimentVariant)}

      {shouldRenderBuilderBlocks()
        ? includedChildren?.map((child, index) => (
            <BuilderBlocks
              key={uniqueId()}
              parentElementId={builderBlock?.id}
              dataPath={`component.options.includedChildren.${index}.item`}
              blocks={child.item}
              style={{ minHeight: 'auto !important' }}
            />
          ))
        : children}
    </div>
  )

  switch (visibilityBasedOn) {
    case 'experiment':
      if (experimentFlag && experimentVariant) {
        const isExperimentVisible = isUserInExperiment(
          userExperiments,
          experimentFlag,
          experimentVariant,
          showAsOff,
          experimentContext?.experimentClient
        )

        if (isExperimentVisible || edit) {
          return experimentConditionalContent
        }
      }
      break
    case 'queryParams':
      if (hasQueryParam(matchType, queryParams) || edit) {
        return queryParamsConditionalContent
      }
      break
    case 'locale':
      return localeConditionalContent
    case 'loggedIn':
      return (
        <LoggedInConditionalContent
          attributes={attributes}
          className={className}
          showIfLoggedIn={showIfLoggedIn}
        >
          {children}
        </LoggedInConditionalContent>
      )
    case 'hasBodiAccess':
      return (
        <BODiAccessConditionalContent
          attributes={attributes}
          className={className}
          showIfHasBodiAccess={showIfHasBodiAccess}
        >
          {children}
        </BODiAccessConditionalContent>
      )
    case 'sku':
      return (
        <SKUinCartConditionalContent
          attributes={attributes}
          className={className}
          skusInOut={skusInOut}
          anyOrAll={anyOrAll}
          skus={skus}
        >
          {children}
        </SKUinCartConditionalContent>
      )
    case 'shopifyEnabled':
      return (
        <ShopifyEnabledConditionalContent
          attributes={attributes}
          className={className}
          showIfShopifyEnabled={showIfShopifyEnabled}
        >
          {children}
        </ShopifyEnabledConditionalContent>
      )
    default:
      return null
  }
}
